import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import {
  StopIcon,
  DownloadIcon,
  CheckCircleIcon,
  RefreshIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  DocumentIcon,
  DeviceMobileIcon,
} from "@heroicons/react/outline";

import BorderLayout from "layouts/BorderLayout";
import { ProjectBreadcrumbPage } from "components/ProjectBreadcrumbPage";
import Button from "components/Button";
import { FullWidthLoadingSpinner } from "components/FullWidthLoadingSpinner";
import { ProjectNotFound } from "components/hosted-scraping/ProjectNotFound";
import StatsCard, { StatItem } from "components/StatsCard";
import DPJobUrlsTableSection from "components/hosted-scraping/DPJobUrlsTableSection";
import { useJobSubtasks } from "hooks/hosted-scraping/useJobSubtasks";
import { useHostedScrapingProjects } from "providers/HostedScrapingProvider";
import { AsyncApiParams, Job, ProjectConfigWithStats } from "providers/HostedScrapingProvider/types";
import scraperApi from "api";
import Toaster from "components/Toaster";
import { PureTooltip } from "components/Tooltip";
import { hasErrorReport, hasResult, isJobActive, jobErrorMessageToHumanReadable, jobErrorReportUrl, jobResultUrl, resultFormatLabel } from "components/hosted-scraping/job-list/utils";
import { frontendJobStatusForJob } from "components/hosted-scraping/job-list/JobStatus";
import { JobErrorMessage } from "components/hosted-scraping/job-list/JobErrorMessage";
import { formatDate } from "components/hosted-scraping/ProjectFormatDate";
import { formatCreditsSpent } from "components/hosted-scraping/creditsSpent";
import { formatTimeAgo } from "utils/dateUtils";
import { isAsyncUrlsLlmProject, isSDEProject, sdeOutputFormatLabels } from "sdecontent";
import { PARSE_RESULTS_LABELS, parseResultsValue } from "components/hosted-scraping/edit-project-components/ParseResultsDropdown";

// Same wording as the project's output format settings (SDE dropdown, or "Parse results" for URL projects).
const outputFormatLabel = (apiParams: AsyncApiParams | null | undefined, projectType: string): string => {
  if (isSDEProject(projectType)) {
    const format = String(apiParams?.outputFormat ?? "json");
    return sdeOutputFormatLabels[format] ?? format.toUpperCase();
  }
  return PARSE_RESULTS_LABELS[parseResultsValue(apiParams, isAsyncUrlsLlmProject(projectType))];
};

const deviceTypeLabel = (deviceType: unknown): string => {
  if (!deviceType) return "Desktop";
  const s = String(deviceType);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

const buildJobStats = (job: Job): StatItem[] => [
  { icon: CheckCircleIcon, iconClassName: "w-5 h-5 text-green-500", label: "Scraped successfully", value: job.successfulTasks },
  {
    icon: RefreshIcon,
    iconClassName: "w-5 h-5 text-blue-500",
    label: "In progress",
    value: job.inProgressTasks,
    showSpinner: job.status === "running" && job.inProgressTasks > 0,
  },
  { icon: XCircleIcon, iconClassName: "w-5 h-5 text-gray-500", label: "Stopped", value: job.cancelledTasks },
  { icon: ExclamationCircleIcon, iconClassName: "w-5 h-5 text-red-500", label: "Failed", value: job.failedTasks },
];

const JobStatsParameters = ({ job, projectType }: { job: Job; projectType: string }) => {
  const apiParams = job.apiParams;
  return (
    <>
      <span className="inline-flex items-center px-3 py-1.5 text-xs rounded-md">
        <DocumentIcon className="w-3 h-3 mr-1.5" />
        Output format: {outputFormatLabel(apiParams, projectType)}
      </span>
      <span className="inline-flex items-center px-3 py-1.5 text-xs rounded-md">
        <DeviceMobileIcon className="w-3 h-3 mr-1.5" />
        Device type: {deviceTypeLabel(apiParams?.deviceType)}
      </span>
      <span className="inline-flex items-center px-3 py-1.5 text-xs rounded-md">
        <RefreshIcon className="w-3 h-3 mr-1.5" />
        Retry 404: {apiParams?.retry404 ? "Enabled" : "Disabled"}
      </span>
      {!isJobActive(job) && hasErrorReport(job) && (
        <span className="ml-auto pr-3">
          <Button text="DOWNLOAD ERROR REPORT" href={jobErrorReportUrl(job.publicJobId)} icon={{ Icon: DownloadIcon, size: "w-4 h-4" }} className="button button-tertiary" size="SM" />
        </span>
      )}
    </>
  );
};

const downloadLabel = (job: Job): string => `DOWNLOAD (${resultFormatLabel(job)})`;

// Same status as the job list, so a "done" job where every URL failed reads as failed here too.
const finishedStatus = (job: Job): { text: string; className: string } => {
  const when = job.finishedAt ? ` on ${formatDate(job.finishedAt)}` : "";
  switch (frontendJobStatusForJob(job)) {
    case "failed": return { text: "Job failed", className: "text-red-600" };
    case "cancelled": return { text: "Job stopped", className: "text-gray-500" };
    case "partially_completed": return { text: `Partially completed${when}`, className: "text-warning-600" };
    default: return { text: `Finished job${when}`, className: "text-success-600" };
  }
};

const sourceInputUrl = (project: ProjectConfigWithStats): string | undefined => {
  const { input } = project;
  if (input.type === "json_url" || input.type === "webhook_input") return input.url || undefined;
  return undefined;
};

function JobDetailsPage({ projectId, jobId }: { projectId: number; jobId: number }) {
  const {
    currentProject,
    currentProjectLoading,
    fetchCurrentProject,
    clearCurrentProject,
    jobs,
  } = useHostedScrapingProjects();

  const jobFromList = jobs?.find((j) => j.id === jobId) ?? null;

  // The subtasks poll (5s) is fresher than the project list's (10s).
  const subtasksState = useJobSubtasks(projectId, jobId);
  const job = subtasksState.job ?? jobFromList;
  const isRunning = job?.status === "running";
  const isCancelling = job?.status === "cancelling";
  // All URLs done but the job still open = backend is aggregating the final result.
  const isFinalizing = isRunning && job.inProgressTasks === 0;
  const [stopRequested, setStopRequested] = useState(false);
  const isStopping = isCancelling || (isRunning && stopRequested);
  // Jobs that never ran (e.g. insufficient credits) have no URLs; show the job list's explanation instead.
  const errorDetails = job?.status === "error" ? jobErrorMessageToHumanReadable(job.result?.errorMessage) : null;

  useEffect(() => {
    fetchCurrentProject(projectId);
    const timer = setInterval(() => fetchCurrentProject(projectId), 10_000);
    return () => {
      clearInterval(timer);
      clearCurrentProject();
    };
  }, [projectId, fetchCurrentProject, clearCurrentProject]);

  const handleStop = async () => {
    if (!job) return;
    setStopRequested(true);
    try {
      await scraperApi.hostedScraping.cancelJob(projectId, job.id);
      Toaster.info("Stopping job… URLs already in progress will finish their current attempt first.");
      fetchCurrentProject(projectId);
      subtasksState.refetch();
    } catch {
      setStopRequested(false);
      Toaster.error("Failed to stop job. Please try again.");
    }
  };

  // A late response for a previously viewed project can land in the shared provider state.
  const project = currentProject?.id === projectId ? currentProject : undefined;
  if (!project && (currentProjectLoading || currentProject)) {
    return <FullWidthLoadingSpinner />;
  }

  if (!project) {
    return <ProjectNotFound />;
  }

  const sourceUrl = sourceInputUrl(project);
  const runningStatusText = job && (
    <div className="flex flex-col items-end gap-0.5 text-xs font-medium text-right">
      <span className="text-neutral-900 whitespace-nowrap">
        Started scraping {formatTimeAgo(job.startedAt || job.createdAt, { fallback: "" })}
      </span>
      {sourceUrl && (
        <span className="text-gray-600 whitespace-nowrap">{sourceUrl}</span>
      )}
    </div>
  );

  const finished = job ? finishedStatus(job) : null;
  const finishedStatusText = job && finished && (
    <div className="flex flex-col items-end gap-0.5 text-xs font-medium text-right">
      <span className={`whitespace-nowrap ${finished.className}`}>
        {finished.text}
      </span>
      <span className="text-neutral-700 whitespace-nowrap">Cost: {formatCreditsSpent(job.credits)}</span>
    </div>
  );

  const titleButtons = isStopping ? (
    <PureTooltip content="URLs already in progress finish their current attempt before stopping.">
      <Button
        text="STOPPING…"
        loading
        disabled
        className="bg-red-600 text-white rounded px-4 py-2 opacity-80"
      />
    </PureTooltip>
  ) : isFinalizing ? (
    <Button
      text="FINALIZING…"
      loading
      disabled
      className="bg-gray-400 text-white rounded px-4 py-2 opacity-80"
    />
  ) : isRunning ? (
    <div className="flex items-center gap-2">
      {runningStatusText}
      <Button
        text="STOP"
        icon={{ Icon: StopIcon, size: "w-6 h-6" }}
        className="bg-red-600 hover:bg-red-700 text-white rounded px-4 py-2"
        onClick={handleStop}
      />
    </div>
  ) : job ? (
    <div className="flex items-center gap-2">
      {finishedStatusText}
      {hasResult(job) ? (
        <Button text={downloadLabel(job)} href={jobResultUrl(job.publicJobId)} icon={{ Icon: DownloadIcon, size: "w-6 h-6" }} className="button button-primary px-6 whitespace-nowrap" />
      ) : (
        <Button text="DOWNLOAD" icon={{ Icon: DownloadIcon, size: "w-6 h-6" }} className="button button-primary px-6" disabled />
      )}
    </div>
  ) : (
    <></>
  );

  return (
    <BorderLayout layout="vertical">
      <ProjectBreadcrumbPage
        readonly
        projectName={project.name}
        projectId={project.id}
        jobLabel={`Job ${jobId}`}
        titleButtons={titleButtons}
        className="bg-slate-50"
      >
        <div className="flex flex-col gap-5 md:px-14 pt-10 pb-24 overflow-auto">
          {job && (
            <StatsCard stats={buildJobStats(job)} parameters={<JobStatsParameters job={job} projectType={project.config.type} />} />
          )}

          {errorDetails ? (
            <JobErrorMessage errorMessage={errorDetails.message} showCTAButtons={false} />
          ) : (
            <DPJobUrlsTableSection projectId={projectId} jobId={jobId} {...subtasksState} stopping={isStopping} />
          )}
        </div>
      </ProjectBreadcrumbPage>
    </BorderLayout>
  );
}

// Keyed so switching jobs mounts a fresh page instead of briefly showing the previous job.
export default function ProjectJobDetails() {
  const { projectId: projectIdParam, jobId: jobIdParam } = useParams() as {
    projectId: string;
    jobId: string;
  };
  const projectId = parseInt(projectIdParam);
  const jobId = parseInt(jobIdParam);

  if (isNaN(projectId) || isNaN(jobId)) {
    return <Navigate to="/projects" replace />;
  }

  return <JobDetailsPage key={`${projectId}-${jobId}`} projectId={projectId} jobId={jobId} />;
}
